import {useEffect, useMemo, useRef, useState} from "react";
import * as maplibre from "@vis.gl/react-maplibre";
import {FeatureCollection} from "geojson";
import '../../assets/css/GeoMap.css';
import * as mapDefaults from "../../static/js/Defaults";
import ColorsDescriptionPanel from "../../components/geo/ColorsDescriptionPanel";
import "../../assets/css/ScrollBar.css";
import MenuPanel from "../../components/geo/MenuPanel";
import {useLanguage} from '../../components/hooks/LanguageContext';
import * as drf from "../../static/js/DataRequestFunctions";
import * as geoService from "../../static/js/GeoMapService";
import DrawingToolsMenu from "../../components/geo/DrawingToolsMenu"
import DrawingManager from "../../static/js/DrawingManager.js";
import { useHexagonData } from '../../components/hooks/useHexagonData';
import MapLoadingSpinner from '../../components/geo/MapLoadingSpinner';
import {
    buildNeighborMap,
    createEmptyMapObjectValues,
    createHexes,
    smoothValuesMap
} from "../../static/js/DataAlgoritms";

import { useFlash } from '../../components/hooks/useFlash';
import { flashLayerConfig } from '../../static/js/flashConfig';

/** @deprecated @type number */
const hexSize = 8;

function GeoMap() {

    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
    const [selectedObjectValue, setSelectedObjectValue] = useState(undefined);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [selectedYear, setSelectedYear] = useState(undefined);

    /** @type {[{string: number}, React.Dispatch<React.SetStateAction<{string: number}>>]} */
    const [valuesMap, setValuesMap] = useState({});

    /** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
    const [mapViews, setMapViews] = useState([]);

    /** @type {[Product[], React.Dispatch<React.SetStateAction<Product[]>>]} */
    const [products, setProducts] = useState([]);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [currentProductIndex, setCurrentProductIndex] = useState(undefined);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [selectedFeatureId, setSelectedFeatureId] = useState(undefined);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [selectedLayerId, setSelectedLayerId] = useState(undefined);

    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
    const [selectedObjectId, setSelectedObjectId] = useState(null);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [openLayer, setOpenLayer] = useState(null);

    /** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
    const [month, setMonth] = useState(6);

    /** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
    const [currentObjectData, setCurrentObjectData] = useState(null);

    /** @type string */
    const {language} = useLanguage();

    /** @type React.MutableRefObject<null> */
    const mapContainerRef = useRef(null);

    /** @type React.MutableRefObject<maplibre.MapRef> */
    const mapRef = useRef(null);
    const [hexSize, setHexSize] = useState(8);

    const [isLayerDataLoading, setIsLayerDataLoading] = useState(false);

    /** @type {[string|null, React.Dispatch<React.SetStateAction<string|null>>]} */
    const [currentDrawingTool, setCurrentDrawingTool] = useState('select');

    /** @type {[React.MutableRefObject<DrawingManager|null>, Function]} */
    const drawingManagerRef = useRef(null);

    const [baseMapViews, setBaseMapViews] = useState([]);
    const [smoothedMapViews, setSmoothedMapViews] = useState([]);
    const neighborMapRef = useRef(null);
    const [selectionMode, setSelectionMode] = useState('single');
    const [hexValues, setHexValues] = useState({});
    const [labelsKey, setLabelsKey] = useState(0);
    const pendingFlyRef = useRef(null);
    const lastFlownKeyRef = useRef(null);
    const currentProductIdRef = useRef(null);
    const loadGenRef = useRef(0);
    const lastHexLoadKeyRef = useRef(null);

    const getMapInstance = () =>
        mapRef.current?.getMaplibreMap?.() || mapRef.current?.getMap?.() || mapRef.current;

    const flyMapTo = (center, zoom = 9) => {
        const map = getMapInstance();
        const flyer = (map && typeof map.flyTo === 'function')
            ? map
            : (mapRef.current && typeof mapRef.current.flyTo === 'function' ? mapRef.current : null);
        if (flyer) {
            flyer.flyTo({ center, zoom, speed: 0.8 });
            pendingFlyRef.current = null;
        } else {
            pendingFlyRef.current = { center, zoom };
        }
    };

    const { flashOutline, isFlashing, startFlash } = useFlash(2000);


    const {
        pickedAreaData,
        selectedObjectIds,
        isLoading,
        handleHexagonSelection,
        reloadCurrentSelection,
        generateLabels,
        clusterData
    } = useHexagonData(selectedFeatureId, selectedYear, month);


    useEffect(() => {
        if (selectedObjectIds.length > 0 && mapViews.length > 0) {
            reloadCurrentSelection(mapViews);
        }
    }, [selectedObjectIds, selectedFeatureId, selectedYear, month, selectedLayerId, mapViews, valuesMap, reloadCurrentSelection]);

    useEffect(() => {
        if (mapRef.current && mapViews.length > 0 && !drawingManagerRef.current) {
            drawingManagerRef.current = new DrawingManager(
                mapRef,
                mapViews,
                setMapViews,
                clearHighlight,
                handleHexagonSelection
            );
            drawingManagerRef.current.setSelectionMode(selectionMode);
            drawingManagerRef.current.setTool(currentDrawingTool);
        }
    }, [mapRef.current, mapViews.length]);

    useEffect(() => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.mapViews = mapViews;
        }
    }, [mapViews]);

    useEffect(() => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setTool(currentDrawingTool);
        }
    }, [currentDrawingTool]);

    useEffect(() => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setSelectionMode(selectionMode);
        }
    }, [selectionMode]);

    useEffect(() => {
        if (selectionMode === 'single' && selectedObjectIds.length === 1) {
            setSelectedObjectValue(selectedObjectIds[0]);
            setSelectedObjectId(selectedObjectIds[0]);
        } else if (selectionMode === 'single' && selectedObjectIds.length === 0) {
            setSelectedObjectValue(null);
            setSelectedObjectId(null);
        }
    }, [selectionMode, selectedObjectIds]);

    useEffect(() => {
        if (!selectedObjectValue || !selectedLayerId || !selectedYear) return;
        loadFullLayerData(selectedObjectValue, selectedLayerId).catch(console.error);
    }, [selectedObjectValue, selectedLayerId, selectedYear, month]);

    useEffect(() => {
        if (selectedFeatureId == null || selectedYear == null || month == null || !products.length) return;
        const product = products[currentProductIndex];
        if (!product) return;
        const regionId = product.region.id;
        const loadKey = `${product.id}-${regionId}-${selectedFeatureId}-${selectedLayerId}-${selectedYear}-${month}-${hexSize}`;
        if (lastHexLoadKeyRef.current === loadKey) {
            // #region agent log
            fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'lang-fix',hypothesisId:'H-LANG',location:'GeoMap.jsx:feature-load',message:'hex reload skipped',data:{loadKey,productId:product.id,regionId},timestamp:Date.now()})}).catch(()=>{});
            // #endregion
            return;
        }
        lastHexLoadKeyRef.current = loadKey;
        let currentFeatures = [];

        const gen = ++loadGenRef.current;
        setIsLayerDataLoading(true);

        drf.getProductMapObjects(regionId, hexSize)
            .then(featureCollection => {
                if (gen !== loadGenRef.current) return null;
                currentFeatures = featureCollection.features;
                return drf.getValuesOfFeatureByYear(
                    selectedFeatureId,
                    Number.parseInt(regionId),
                    selectedYear,
                    month,
                    hexSize,
                    false
                );
            })
            .then(response => {
                if (response == null || gen !== loadGenRef.current) return;
                const updatedMap = {};
                Object.keys(response).forEach(key => updatedMap[key] = response[key] ?? null);
                const isCompetitors = geoService.isCompetitorsLayer(products, currentProductIndex, selectedLayerId);
                const layer = product.layers?.find(l => String(l.id) === String(selectedLayerId));
                const hexes = createHexes(updatedMap, currentFeatures, isCompetitors, false);
                // #region agent log
                fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'lang-fix',hypothesisId:'H-LANG',location:'GeoMap.jsx:feature-load',message:'layer paint',data:{loadKey,gen,productId:product.id,regionId,fullName:layer?.full_name,hexCount:hexes.length},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
                setMapViews(hexes);
                setValuesMap(updatedMap);
            })
            .catch(error => {
                console.error("Error loading feature data:", error);
            })
            .finally(() => {
                if (gen === loadGenRef.current) setIsLayerDataLoading(false);
            });
    }, [selectedFeatureId, selectedYear, month, currentProductIndex, products, hexSize]);

    useEffect(() => {
        setSelectedObjectValue(null);
        setSelectedObjectId(null);
    }, [hexSize]);

    useEffect(() => {
        if (!mapViews.length) return;
        const product = products[currentProductIndex];
        const region = product?.region;
        const flyKey = `${product?.id ?? 'none'}-${region?.id ?? 'none'}-${hexSize}`;
        if (lastFlownKeyRef.current === flyKey) return;

        const regionCenter =
            Number.isFinite(region?.lon) && Number.isFinite(region?.lat)
                ? [region.lon, region.lat]
                : null;
        const hexCenter = geoService.getCentralHexCenter(mapViews);
        const center = regionCenter || hexCenter?.center;
        if (!center) return;
        lastFlownKeyRef.current = flyKey;
        flyMapTo(center);
    }, [mapViews, hexSize, currentProductIndex, products]);

    useEffect(() => {
        drf.getUserProductsList(language)
            .then(response => {
                const list = response || [];
                const savedId = currentProductIdRef.current;
                const idx = savedId != null ? list.findIndex((p) => p.id === savedId) : 0;
                const nextIndex = idx >= 0 ? idx : 0;
                // #region agent log
                fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'lang-fix',hypothesisId:'H-LANG',location:'GeoMap.jsx:load-products',message:'products reloaded',data:{savedId,nextIndex,count:list.length,sameProduct:savedId===list[nextIndex]?.id},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
                setProducts(list);
                handleProductSelect(nextIndex, list);
            })
            .catch(() => console.log('err'));
    }, [language]);

    useEffect(() => {
        if (!selectedObjectId) return;
        let growing = true;
        let width = 4;
        const interval = setInterval(() => {
            width = growing ? width + 0.15 : width - 0.15;
            if (width >= 6) growing = false;
            if (width <= 4) growing = true;
            const map = document.querySelector(".maplibregl-map")?.map;
            if (map?.setPaintProperty) {
                map.setPaintProperty("highlight-layer", "line-width", width);
            }
        }, 50);
        return () => clearInterval(interval);
    }, [selectedObjectId]);

    useEffect(() => {
        if (drawingManagerRef.current && selectedObjectIds.length > 0) {
            drawingManagerRef.current.updateHexagonHighlight();
        }
    }, [mapViews]);

    useEffect(() => {
        const map = mapRef.current?.getMaplibreMap?.() || mapRef.current?.getMap?.() || mapRef.current;
        if (map) {
            try {
                map.moveLayer('labels-layer');
                map.moveLayer('highlight-outline-layer');
                map.moveLayer('flash-glow-layer');
                map.moveLayer('flash-outline-layer');
            } catch (e) {}
        }
    }, [mapViews, flashOutline]);

    useEffect(() => {
        const hasSelection = selectedObjectIds.length > 0;
        if (hasSelection && smoothedMapViews.length > 0) {
            setMapViews(smoothedMapViews);
        } else if (!hasSelection && baseMapViews.length > 0) {
            setMapViews(baseMapViews);
        }
    }, [selectedObjectIds, baseMapViews, smoothedMapViews]);

    useEffect(() => {
        const map = mapRef.current?.getMaplibreMap?.() || mapRef.current?.getMap?.() || mapRef.current;
        if (map) {
            try {
                map.moveLayer('labels-layer');
                map.moveLayer('highlight-outline-layer'); // ← добавить эту строку
            } catch (e) {}
        }
    }, [mapViews]);

    // const extractClusterIds = (cluster) => {
    //     if (!cluster) return [];
    //     const arr = Array.isArray(cluster) ? cluster : [cluster];
    //     return arr
    //         .map(item => {
    //             if (item && typeof item === 'object') {
    //                 return item.properties?.object_id ?? item.object_id ?? item.id;
    //             }
    //             return item;
    //         })
    //         .filter(id => id != null)
    //         .map(String);
    // };

    // const flashArea = (cluster) => {
    //     if (flashTimeoutRef.current) {
    //         clearTimeout(flashTimeoutRef.current);
    //     }
    //     const ids = extractClusterIds(cluster);
    //     setFlashFeatures(ids);
    //     flashTimeoutRef.current = setTimeout(() => {
    //         setFlashFeatures([]);
    //         flashTimeoutRef.current = null;
    //     }, 2000); // 2 секунды подсветки
    // };

    // useEffect(() => {
    //     return () => {
    //         if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    //     };
    // }, []);


    const loadFullLayerData = async (objectId, layerId) => {
        if (!objectId || !layerId || !selectedYear || !month) return {};
        try {
            const layer = products[currentProductIndex]?.layers?.find(l => l.id === layerId);
            if (!layer?.features) return {};
            const allData = await geoService.getLayerData(layer, objectId, selectedYear, month);
            return allData;
        } catch (error) {
            console.error("Error loading full layer data:", error);
            return {};
        }
    };

    /**
     *
     * @param featureCollection {FeatureCollection}
     * @param product {Product}
     * @param newRegion {GeoRegion}
     */
    function resolveRegionOfProduct(featureCollection, product, newRegion, gen) {
        if (gen != null && gen !== loadGenRef.current) return;
        setValuesMap(createEmptyMapObjectValues(featureCollection.features));
        setMapViews(featureCollection.features);
        if (product.layers?.length === 0) return;
        const firstLayer = product.layers[0];
        setSelectedLayerId(firstLayer.id);
        if (firstLayer.features?.length === 0) return;
        const firstFeature = firstLayer.features[0];
        setSelectedFeatureId(firstFeature.id);
        drf.getArrayOfYearsOfFeature(firstFeature.id, newRegion.id)
            .then(arr => {
                if (arr.length > 0) setSelectedYear(arr[0]);
            });
    }

    const applyProductLayers = (product) => {
        const firstLayer = product.layers?.[0];
        if (!firstLayer) {
            setSelectedLayerId(undefined);
            setSelectedFeatureId(undefined);
            return;
        }
        setSelectedLayerId(firstLayer.id);
        setSelectedFeatureId(firstLayer.features?.[0]?.id);
    };

    const handleProductSelect = (index, list = products) => {
        const product = list[index];
        if (!product) return;
        /** @type GeoRegion */ const newRegion = product.region;
        const prevId = currentProductIdRef.current;
        const sameProduct = prevId === product.id;
        const sameIndex = index === currentProductIndex && sameProduct;
        const prevRegionId = list.find((p) => p.id === prevId)?.region?.id
            ?? products[currentProductIndex]?.region?.id;
        const sameRegion = newRegion?.id === prevRegionId;
        currentProductIdRef.current = product.id;
        // #region agent log
        fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'lang-fix',hypothesisId:'H-LANG',location:'GeoMap.jsx:handleProductSelect',message:'product select',data:{index,productId:product.id,name:product.full_name,region:newRegion?.name,sameIndex,sameProduct,sameRegion},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
        if (sameIndex) {
            const center = Number.isFinite(newRegion?.lon) && Number.isFinite(newRegion?.lat)
                ? [newRegion.lon, newRegion.lat]
                : null;
            if (center) flyMapTo(center);
            return;
        }
        if (sameProduct) {
            setCurrentProductIndex(index);
            return;
        }
        lastFlownKeyRef.current = null;
        const gen = ++loadGenRef.current;
        setCurrentProductIndex(index);
        setValuesMap({});
        setSelectedObjectValue(undefined);
        if (sameRegion) {
            applyProductLayers(product);
            return;
        }
        setSelectedLayerId(undefined);
        setSelectedFeatureId(undefined);
        geoService.onRegionChange(newRegion, (fc, p, r) => resolveRegionOfProduct(fc, p, r, gen), product, hexSize);
    };

    const handleSelectionLayer = (layerId) => {
        const product = products[currentProductIndex];
        const layer = product.layers.find(l => l.id.toString() === layerId.toString());
        if (!layer) return;
        setSelectedLayerId(layer.id);
        setSelectedFeatureId(layer.features[0]?.id);
        if (selectedObjectValue) {
            loadFullLayerData(selectedObjectValue, layer.id).catch(console.error);
        }
    };

    const handleMapMouseAction = (e) => {
        if (drawingManagerRef.current && currentDrawingTool) {
            drawingManagerRef.current.handleClick(e);

            e.originalEvent?.stopPropagation();
            return;
        }

        // if (currentDrawingTool === 'select') return;
        //
        // const { lngLat } = e;
        // const matched = geoService.getIntersectsObjects(mapViews, lngLat);
        // if (!matched.length) {
        //     clearHighlight();
        //     return;
        // }
        // const objectId = matched[0].properties.object_id;
        // if (objectId === selectedObjectId) {
        //     clearHighlight();
        //     return;
        // }
        // setCurrentObjectData(null);
        // setPickedAreaData(null);
        // setSelectedObjectValue(objectId);
        // setSelectedObjectId(objectId);
        // setMapViews(prev => prev.map(feature => geoService.highlightFeature(feature, objectId)));
        // if (!selectedLayerId) return;
        // loadFullLayerData(objectId, selectedLayerId).then(data => {
        //     if (data) {
        //         setCurrentObjectData(data);
        //         setPickedAreaData(data);
        //     }
        // });
    };

    const clearHighlight = () => {
        setSelectedObjectValue(null);
        setSelectedObjectId(null);

    };

    const handleMapMouseMove = (e) => {
        if (drawingManagerRef.current && drawingManagerRef.current.isDrawing) {
            drawingManagerRef.current.handleMouseMove(e);
        }
    };

    const handleMapDoubleClick = (e) => {
        if (drawingManagerRef.current && currentDrawingTool) {
            drawingManagerRef.current.handleDoubleClick(e);
        }
    };

    const handleMapContextMenu = (e) => {
        if (drawingManagerRef.current && currentDrawingTool) {
            return drawingManagerRef.current.handleContextMenu(e);
        }
    };



    const handleSelectTool = (tool) => {
        setCurrentDrawingTool(tool);
        console.log('Selected tool:', tool);
    };

    const handleClearSelection = () => {
        handleHexagonSelection(null);

        if (drawingManagerRef.current) {
            drawingManagerRef.current.clearSelectionOnly();
        }

        setSelectedObjectValue(null);
        setSelectedObjectId(null);

    };

    useEffect(() => {
        if (drawingManagerRef.current && selectedObjectIds.length > 0) {
            drawingManagerRef.current.updateHexagonHighlight();
        }
    }, [mapViews, selectedObjectIds]);

    const labelFeatures = useMemo(() => {
        const features = generateLabels();
        return { type: 'FeatureCollection', features };
    }, [generateLabels, clusterData]);

    return (
        <div className={"map-page"}>
            <MenuPanel
                products={products}
                currentProductIndex={currentProductIndex}
                setCurrentProductIndex={handleProductSelect}
                year={selectedYear}
                setYear={setSelectedYear}
                month={month}
                language={language}
                setMonth={setMonth}
                selectedFeatureId={selectedFeatureId}
                setSelectedFeatureId={setSelectedFeatureId}
                selectedLayerId={selectedLayerId}
                setSelectedLayerId={setSelectedLayerId}
                openLayer={openLayer}
                setOpenLayer={setOpenLayer}
                handleSelectionLayer={handleSelectionLayer}
                handleSelectionFeature={setSelectedFeatureId}
                selectedObjectValue={selectedObjectValue}
                pickedAreaData={pickedAreaData}
                hexSize={hexSize}
                setHexSize={setHexSize}
                selectedObjectIds={selectedObjectIds}
                isLoading={isLoading}
                clusterData={clusterData}
                onClusterSelect={handleHexagonSelection}
                onClusterFlash={startFlash}
            />

            <ColorsDescriptionPanel currentH3s={valuesMap} isCompetitors={geoService.isCompetitorsLayer(products, currentProductIndex, selectedLayerId)}/>

            <DrawingToolsMenu
                onSelectTool={handleSelectTool}
                onClearSelection={handleClearSelection}
                currentTool={currentDrawingTool}
                selectionMode={selectionMode}
                onSelectionModeChange={setSelectionMode}
            />

            <div className={"map-container"} ref={mapContainerRef}>
                <maplibre.Map
                    ref={mapRef}
                    initialViewState={{
                        longitude: mapDefaults.defaultMapViewState.longitude,
                        latitude: mapDefaults.defaultMapViewState.latitude,
                        zoom: mapDefaults.defaultMapViewState.zoom,
                    }}
                    doubleClickZoom={false}
                    onClick={handleMapMouseAction}
                    onMouseMove={handleMapMouseMove}
                    onDblClick={handleMapDoubleClick}
                    onContextMenu={handleMapContextMenu}
                    onLoad={() => {
                        const map = getMapInstance();
                        if (pendingFlyRef.current && map && typeof map.flyTo === 'function') {
                            map.flyTo({
                                center: pendingFlyRef.current.center,
                                zoom: pendingFlyRef.current.zoom,
                                speed: 0.8,
                            });
                            pendingFlyRef.current = null;
                        }
                        if (map) {
                            try {
                                if (map.getLayer('labels-layer')) {
                                    map.moveLayer('labels-layer');
                                }
                                if (map.getLayer('highlight-outline-layer')) {
                                    map.moveLayer('highlight-outline-layer');
                                }
                            } catch (e) {}
                        }
                    }}
                    mapStyle={"https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"}>
                    {mapViews.length > 0 && (
                        <maplibre.Source
                            key={`source-${selectedFeatureId}-${selectedYear}-${hexSize}`}
                            id="area-source"
                            type="geojson"
                            promoteId="object_id"
                            data={{ type: 'FeatureCollection', features: mapViews }}
                        >
                            <maplibre.Layer {...mapDefaults.defaultLayerProperties} />
                        </maplibre.Source>
                    )}

                    {flashOutline.features.length > 0 && (
                        <maplibre.Source
                            id="flash-outline-source"
                            type="geojson"
                            data={flashOutline}
                        >
                            <maplibre.Layer
                                id={flashLayerConfig.glow.id}
                                type="line"
                                layout={{ "line-join": "round", "line-cap": "round" }}
                                paint={{
                                    "line-color": flashLayerConfig.glow.paint["line-color"],
                                    "line-width": flashLayerConfig.glow.paint["line-width"],
                                    "line-blur": flashLayerConfig.glow.paint["line-blur"],
                                    "line-opacity": isFlashing ? flashLayerConfig.glow.paint["line-opacity"] : 0,
                                    "line-opacity-transition": { duration: 400, delay: 0 },
                                }}
                            />
                            <maplibre.Layer
                                id={flashLayerConfig.outline.id}
                                type="line"
                                layout={{ "line-join": "round", "line-cap": "round" }}
                                paint={{
                                    "line-color": flashLayerConfig.outline.paint["line-color"],
                                    "line-width": flashLayerConfig.outline.paint["line-width"],
                                    "line-blur": flashLayerConfig.outline.paint["line-blur"],
                                    "line-opacity": isFlashing ? flashLayerConfig.outline.paint["line-opacity"] : 0,
                                    "line-opacity-transition": { duration: 400, delay: 0 },
                                }}
                            />
                        </maplibre.Source>
                    )}

                    {labelFeatures && labelFeatures.features?.length > 0 && (
                        <maplibre.Source
                            id="labels-source"
                            type="geojson"
                            data={labelFeatures}
                        >
                            <maplibre.Layer
                                id="labels-layer"
                                type="symbol"
                                layout={{
                                    "text-field": ["get", "label"],
                                    "text-size": [
                                        "interpolate",
                                        ["linear"],
                                        ["zoom"],
                                        8, 12,
                                        12, 16,
                                        16, 18
                                    ],
                                    "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                                    "text-anchor": "center",
                                    "text-offset": [0, 0],
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                    "text-max-width": 8,
                                }}
                                paint={{
                                    "text-color": "#000000",
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 2,
                                    "text-halo-blur": 2,
                                }}
                            />
                        </maplibre.Source>
                    )}

                    {/* Слой свечения (расплывчатый ореол) */}
                    <maplibre.Source
                        id="highlight-outline-source"
                        type="geojson"
                        data={{ type: "FeatureCollection", features: [] }}
                    >
                        {/* Основной чёткий контур */}
                        <maplibre.Layer
                            id="highlight-outline-layer"
                            type="line"
                            layout={{ "line-join": "round" }}
                            paint={{
                                "line-color": "#ffffff",
                                "line-width": 3,
                                "line-blur": 0,
                                "line-opacity": 0.5,
                            }}
                        />
                        <maplibre.Layer
                            id="highlight-glow-layer"
                            type="line"
                            layout={{ "line-join": "round" }}
                            paint={{
                                "line-color": "rgba(255, 255, 255, 0.8)",
                                "line-width": 15,
                                "line-blur": 8,
                                "line-opacity": 0.5,
                            }}
                        />
                    </maplibre.Source>
                </maplibre.Map>

                {isLayerDataLoading && (
                    <div className="map-loading-overlay">
                        <MapLoadingSpinner />
                    </div>
                )}
            </div>
        </div>
    );
}

GeoMap.link = "/geo-map";
export default GeoMap;
